import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GetSingleOrderDetailsObject } from "../../../@types/DRO/PurchaseOrderLinesResponse";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import CreatePurchaseOrder from "./CreatePurchaseOrder";

const EditPurchaseOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [orderData, setOrderData] =
    useState<GetSingleOrderDetailsObject | null>(null);
  const [loadingError, setLoadingError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Get status from URL to preserve filter when navigating back
  const statusFilter = searchParams.get("status") || "";

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) {
        setLoadingError(true);
        setErrorMessage("No order ID provided");
        return;
      }

      setLoading(true);
      try {
        const response = await ProductOrderApi.api.getSingleProductOrderDetails
        (
          parseInt(id)
        );
        const purchaseOrderLines = response.data.purchaseOrderLines || [];

        // Transform the data to ensure compatibility
        const transformedData = {
          ...response.data,
          purchaseOrderLines: purchaseOrderLines.map((line) => ({
            ...line,
            part: {
              id: line.partId,
              description: line.partName || "Unknown Part",
              referenceCode: line.referenceCode || "",
              price: line.price,
              tax: line.tax || 0,
            },
            quantityOrdered: line.quantityOrdered || 0,
            price: line.price || 0,
            taxes: line.tax || 0,
          })),
        };

        setOrderData(transformedData);
      } catch (error: any) {
        console.error("Error fetching order details:", error);
        setLoadingError(true);
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load order details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [id, navigate, setLoading]);

  const handleBack = () => {
    navigate(`/home${statusFilter ? `?status=${statusFilter}` : ""}`);
  };

  if (loadingError) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          p: 3,
        }}>
        <Paper
          sx={{
            p: 6,
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
            borderRadius: 2,
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}>
            <ErrorIcon sx={{ fontSize: 32, color: "#DC2626" }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 2,
            }}>
            Error Loading Order
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#6B7280",
              mb: 4,
            }}>
            {errorMessage}
          </Typography>

          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              backgroundColor: "#3B82F6",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#2563EB",
              },
            }}>
            Back to Orders
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!orderData) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
        }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={48}
            sx={{
              color: "#3B82F6",
              mb: 2,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: "#6B7280",
              fontWeight: 500,
            }}>
            Loading order details...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <CreatePurchaseOrder
      onBack={handleBack}
      orderData={orderData}
      isEditMode={true}
      statusFilter={statusFilter}
    />
  );
};

export default EditPurchaseOrder;
